import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

interface AvailableWeek {
  week_start: string;
  week_label: string;
  available: boolean;
}

type PageState =
  | { status: 'loading' }
  | { status: 'not_found' }
  | { status: 'already_scheduled'; date: string; firstname: string }
  | { status: 'pick'; weeks: AvailableWeek[]; firstname: string }
  | { status: 'confirming' }
  | { status: 'success'; week_label: string; opmeetbezoek_date: string; firstname: string };

function dutchFullDate(dateStr: string): string {
  const d = new Date(dateStr);
  const months = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export default function InstallationSchedulingPage() {
  const { token } = useParams<{ token: string }>();
  const [page, setPage] = useState<PageState>({ status: 'loading' });
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firstname, setFirstname] = useState('');

  useEffect(() => {
    if (!token) { setPage({ status: 'not_found' }); return; }

    const init = async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/get_available_weeks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: ANON_KEY },
          body: JSON.stringify({ scheduling_token: token }),
        });
        const json = await res.json();
        if (!res.ok || json.error) {
          setPage({ status: 'not_found' });
          return;
        }
        setFirstname(json.lead_firstname || '');
        if (json.already_scheduled) {
          setPage({ status: 'already_scheduled', date: json.date, firstname: json.lead_firstname || '' });
        } else {
          setPage({ status: 'pick', weeks: json.available_weeks || [], firstname: json.lead_firstname || '' });
        }
      } catch {
        setPage({ status: 'not_found' });
      }
    };
    init();
  }, [token]);

  const handleConfirm = async () => {
    if (!selected || !token) return;
    const fn = firstname;
    const wl = page.status === 'pick' ? (page.weeks.find(w => w.week_start === selected)?.week_label || '') : '';
    setPage({ status: 'confirming' });
    setError(null);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/confirm_installation_week`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: ANON_KEY },
        body: JSON.stringify({ scheduling_token: token, week_start: selected }),
      });
      const json = await res.json();
      if (!res.ok || json.error) {
        setError(json.error || 'Er is een fout opgetreden. Probeer opnieuw.');
        setPage({ status: 'pick', weeks: [], firstname: fn }); // re-fetch would be ideal; simplify
      } else if (json.already_scheduled) {
        setPage({ status: 'already_scheduled', date: json.date, firstname: fn });
      } else {
        setPage({ status: 'success', week_label: json.week_label || wl, opmeetbezoek_date: json.opmeetbezoek_date, firstname: fn });
      }
    } catch {
      setError('Er is een fout opgetreden. Controleer uw verbinding.');
      setPage({ status: 'pick', weeks: [], firstname: fn });
    }
  };

  // ── Shared styles ────────────────────────────────────────────────────────────
  const pageStyle: React.CSSProperties = { minHeight: '100vh', background: '#EDE8E0', padding: '2.5rem 1rem 4rem', fontFamily: 'system-ui, -apple-system, sans-serif' };
  const cardStyle: React.CSSProperties = { background: '#F5F0EA', borderRadius: 16, boxShadow: '0 1px 6px rgba(28,25,23,0.08)', border: '1px solid #E8E0D5', overflow: 'hidden' };
  const wordmark = <p style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#A09080', margin: '0 0 1.5rem', fontWeight: 600 }}>PVC Vloer Nederland</p>;

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (page.status === 'loading' || page.status === 'confirming') {
    return (
      <div style={{ ...pageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 32, height: 32, border: '2px solid #C8BDB0', borderTopColor: '#6B5E55', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────────
  if (page.status === 'not_found') {
    return (
      <div style={{ ...pageStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          {wordmark}
          <p style={{ fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 600, color: '#1C1917', marginBottom: 8 }}>Link niet gevonden</p>
          <p style={{ fontSize: 14, color: '#8A7E75' }}>Deze link is ongeldig of verlopen. Neem contact op met uw leverancier.</p>
        </div>
      </div>
    );
  }

  // ── Already scheduled ────────────────────────────────────────────────────────
  if (page.status === 'already_scheduled') {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          {wordmark}
          <div style={{ ...cardStyle, padding: '2rem', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(61,124,61,0.10)', border: '1px solid rgba(61,124,61,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#3D7C3D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <p style={{ fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 600, color: '#1C1917', marginBottom: 8 }}>Al ingepland</p>
            <p style={{ fontSize: 14, color: '#6B5E55', lineHeight: 1.65 }}>
              {page.firstname ? `Beste ${page.firstname}, uw` : 'Uw'} installatieweek is al bevestigd op {dutchFullDate(page.date)}.
            </p>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (page.status === 'success') {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          {wordmark}
          <div style={{ ...cardStyle, padding: '2.5rem 2rem', textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(61,124,61,0.10)', border: '1px solid rgba(61,124,61,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3D7C3D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <p style={{ fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 600, color: '#1C1917', marginBottom: 10 }}>
              {page.firstname ? `Bedankt, ${page.firstname}!` : 'Bevestigd!'}
            </p>
            <p style={{ fontSize: 14, color: '#6B5E55', lineHeight: 1.65, marginBottom: '1.5rem' }}>
              Uw installatieweek is ingepland.
            </p>
            <div style={{ background: '#EDE8E0', borderRadius: 10, padding: '1rem 1.25rem', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '0.75rem', borderBottom: '1px solid #E8E0D5', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: 13, color: '#8A7E75' }}>Installatieweek</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#3D7C3D' }}>{page.week_label}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: '#8A7E75' }}>Opmeetbezoek</span>
                <span style={{ fontSize: 13, color: '#3D3530' }}>{dutchFullDate(page.opmeetbezoek_date)} om 10:00</span>
              </div>
            </div>
            <p style={{ fontSize: 12, color: '#A09080', marginTop: '1rem', lineHeight: 1.55 }}>
              Onze specialist neemt contact met u op om het opmeetbezoek te bevestigen. U ontvangt ook een bevestigingsmail.
            </p>
          </div>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Pick week ────────────────────────────────────────────────────────────────
  const { weeks } = page;

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        {wordmark}

        <div style={cardStyle}>
          {/* Header */}
          <div style={{ padding: '1.75rem 2rem 1.5rem', borderBottom: '1px solid #E8E0D5' }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A09080' }}>Installatieplanning</p>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 600, color: '#1C1917', margin: '0 0 6px' }}>Kies uw installatieweek</h1>
            <p style={{ margin: 0, fontSize: 14, color: '#8A7E75', lineHeight: 1.55 }}>
              {page.firstname ? `Beste ${page.firstname}, kies` : 'Kies'} uw gewenste installatieweek. Wij zorgen voor de rest.
            </p>
          </div>

          {/* Week cards */}
          <div style={{ padding: '1.5rem 2rem' }}>
            {weeks.length === 0 ? (
              <p style={{ fontSize: 14, color: '#8A7E75', textAlign: 'center', padding: '2rem 0' }}>
                Er zijn momenteel geen beschikbare weken. Neem contact op met uw leverancier.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {weeks.map(week => {
                  const isSelected = selected === week.week_start;
                  return (
                    <button
                      key={week.week_start}
                      onClick={() => setSelected(week.week_start)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        padding: '14px 16px', borderRadius: 10, textAlign: 'left',
                        border: `2px solid ${isSelected ? '#3D7C3D' : '#E8E0D5'}`,
                        background: isSelected ? 'rgba(61,124,61,0.06)' : '#fff',
                        cursor: 'pointer', transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        border: `2px solid ${isSelected ? '#3D7C3D' : '#C8BDB0'}`,
                        background: isSelected ? '#3D7C3D' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                      }}>
                        {isSelected && <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1.5 5 4 7.5 8.5 2.5"/></svg>}
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#1C1917' }}>{week.week_label}</p>
                        <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8A7E75' }}>Beschikbaar</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {error && (
              <p style={{ fontSize: 13, color: '#B91C1C', background: 'rgba(185,28,28,0.06)', border: '1px solid rgba(185,28,28,0.15)', borderRadius: 8, padding: '8px 12px', marginTop: '1rem' }}>
                {error}
              </p>
            )}

            <button
              onClick={handleConfirm}
              disabled={!selected || weeks.length === 0}
              style={{
                width: '100%', marginTop: '1.25rem', padding: '13px 0',
                fontSize: 15, fontWeight: 600, borderRadius: 10, border: 'none',
                background: '#3D7C3D', color: '#fff',
                cursor: !selected ? 'not-allowed' : 'pointer',
                opacity: !selected ? 0.45 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              Bevestig mijn installatieweek
            </button>
          </div>
        </div>

        <p style={{ marginTop: '1.5rem', fontSize: 11, color: '#A09080', textAlign: 'center' }}>
          PVC Vloer Nederland B.V.
        </p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
